import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PositionsPage from "@/pages/PositionsPage";
import CandidatesPage from "@/pages/CandidatesPage";
import EmployeesPage from "@/pages/EmployeesPage";
import TestsPage from "@/pages/TestsPage";
import SimulationsPage from "@/pages/SimulationsPage";
import ReportCandidatesPage from "@/pages/ReportCandidatesPage";
import ReportEmployeesPage from "@/pages/ReportEmployeesPage";
import SettingsPage from "@/pages/SettingsPage";
import SupportPage from "@/pages/SupportPage";
import PricingPage from "@/pages/PricingPage";
import PublicTestPage from "@/pages/PublicTestPage";
import PublicApplyPage from "@/pages/PublicApplyPage";
import AuthCallback from "@/pages/AuthCallback";
import CompleteProfilePage from "@/pages/CompleteProfilePage";
import EmployeeRegisterPage from "@/pages/EmployeeRegisterPage";
import SharedReportPage from "@/pages/SharedReportPage";
import InterviewsPage from "@/pages/InterviewsPage";
import CalendarHubPage from "@/pages/CalendarHubPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import AdminWorkspacesPage from "@/pages/AdminWorkspacesPage";
import AdminGenerationsPage from "@/pages/AdminGenerationsPage";
import AdminReportsAffectedPage from "@/pages/AdminReportsAffectedPage";
import AdminAuditLogPage from "@/pages/AdminAuditLogPage";
import AdminReleaseNotesPage from "@/pages/AdminReleaseNotesPage";
import AdminProposalsPage from "@/pages/AdminProposalsPage";
import AdminHealthPage from "@/pages/AdminHealthPage";
import AdminTicketsPage from "@/pages/AdminTicketsPage";
import AdminTermsAcceptancesPage from "@/pages/AdminTermsAcceptancesPage";
import { CondizioniServizioPage, AddendumAgenziePage, PrivacyPage, DpaPage } from "@/legal/LegalPages";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-2 border-[#1B4FD8] border-t-transparent rounded-full" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-2 border-[#1B4FD8] border-t-transparent rounded-full" /></div>;
  if (user) return <Navigate to="/candidates" replace />;
  return children;
}

// BUG-23: redirect dai vecchi URL /test/{candidate|employee}/{token} ai nuovi /analisi/{candidato|dipendente}/{token}
function LegacyTestRedirect({ target }) {
  const { testLink } = useParams();
  return <Navigate to={`${target}/${testLink}`} replace />;
}

function AppRoutes() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route path="/complete-profile" element={<CompleteProfilePage />} />
      <Route path="/employee-register/:inviteCode" element={<EmployeeRegisterPage />} />
      <Route path="/shared-report/:shareId" element={<SharedReportPage />} />
      <Route path="/apply/:linkCode" element={<PublicApplyPage />} />
      {/* B71-TER TASK 1: legal pages — pubbliche, accessibili senza login */}
      <Route path="/legal/condizioni-servizio" element={<CondizioniServizioPage />} />
      <Route path="/legal/addendum-agenzie" element={<AddendumAgenziePage />} />
      {/* B71-QUAT TASK 6: pagina Privacy in-app (sostituisce link rotto a /privacy) */}
      <Route path="/legal/privacy" element={<PrivacyPage />} />
      {/* B71-QUINQUE TASK 7: pagina DPA pubblica (art. 28 GDPR) */}
      <Route path="/legal/dpa" element={<DpaPage />} />
      {/* BUG-23: nuovi path customer-facing /analisi/ + alias deprecati /test/ con redirect */}
      <Route path="/analisi/candidato/:testLink" element={<PublicTestPage testType="candidate" />} />
      <Route path="/analisi/dipendente/:testLink" element={<PublicTestPage testType="employee" />} />
      <Route path="/test/candidate/:testLink" element={<LegacyTestRedirect target="/analisi/candidato" />} />
      <Route path="/test/employee/:testLink" element={<LegacyTestRedirect target="/analisi/dipendente" />} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/employees" element={<EmployeesPage />} />
        <Route path="/positions" element={<PositionsPage />} />
        <Route path="/candidates" element={<CandidatesPage />} />
        <Route path="/tests" element={<TestsPage />} />
        <Route path="/interviews" element={<Navigate to="/calendario" replace />} />
        <Route path="/colloqui" element={<Navigate to="/calendario" replace />} />
        <Route path="/calendario" element={<CalendarHubPage />} />
        <Route path="/calendario/lista" element={<InterviewsPage />} />
        <Route path="/simulazione-assunzione" element={<SimulationsPage />} />
        <Route path="/reports/candidates" element={<ReportCandidatesPage />} />
        <Route path="/reports/employees" element={<ReportEmployeesPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        {/* Admin Console */}
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/workspaces" element={<AdminWorkspacesPage />} />
        <Route path="/admin/generations" element={<AdminGenerationsPage />} />
        <Route path="/admin/reports-affected" element={<AdminReportsAffectedPage />} />
        <Route path="/admin/release-notes" element={<AdminReleaseNotesPage />} />
        <Route path="/admin/proposals" element={<AdminProposalsPage />} />
        {/* Batch 62.B: /admin/certifications-expiring rimosso, ora cliente-side in /employees?tab=certificazioni */}
        <Route path="/admin/certifications-expiring" element={<Navigate to="/employees?tab=certificazioni" replace />} />
        <Route path="/admin/audit-log" element={<AdminAuditLogPage />} />
        <Route path="/admin/tickets" element={<AdminTicketsPage />} />
        {/* B71-SEXIES TASK 9: Console Admin Accettazioni Termini */}
        <Route path="/admin/terms-acceptances" element={<AdminTermsAcceptancesPage />} />
        <Route path="/admin/health" element={<AdminHealthPage />} />
        {/* Backward-compat redirects */}
        <Route path="/dashboard" element={<Navigate to="/candidates" replace />} />
        <Route path="/reports" element={<Navigate to="/reports/candidates" replace />} />
        <Route path="/simulations" element={<Navigate to="/simulazione-assunzione" replace />} />
        <Route path="/test" element={<Navigate to="/reports/candidates" replace />} />
      </Route>
      <Route path="/" element={<Navigate to="/candidates" replace />} />
      <Route path="*" element={<Navigate to="/candidates" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}
